import React, { useEffect, useState } from 'react';
import { Box, ThemeProvider, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from './theme';
import { UserProfile } from './UserProfile';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import * as constants from "../../../constants";
import { useNavigate } from 'react-router-dom';
import { getByParticipantIdLogo } from '../../fiscalUpgrade/services/ParticipantsServices/SaveParticpantService';
import { useTranslation } from 'react-i18next';

type ViewInfoProps = {
    view: string;
};
const TopBar = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isTopView',
})<{ isTopView: boolean }>(({ theme, isTopView }) => ({
    background: theme.palette.primary.main,
    padding: '6px',
    paddingLeft: isTopView ? '6.5%' : '30px',
    paddingRight: isTopView ? '6.5%' : '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'fixed',
    top: 0,
    width: '100%',
    zIndex: theme.zIndex.appBar,
    boxShadow: theme.shadows[4],
    '@media (max-width: 991px)': {
        padding: '0 20px',
    },
}));



const Logo = styled('img')({
    width: '215px',
    height: 'auto',
});

const TopBarActions = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
});

const Row = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '1px',
    marginBottom: "5px",
    marginTop: "5px",
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
});

export const CustomizeHeader: React.FC<ViewInfoProps> = ({ view }) => {
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.auth);
    const isSmallScreen = useMediaQuery('(max-width: 600px)');
    const [logoSrc, setLogoSrc] = useState<string | null>(null);
    let role: any = userDetails.roles?.roleName

    const imageBlob = userDetails?.clientVo?.logo
        ? `data:image/png;base64,${userDetails.clientVo.logo}`
        : null;
    const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);


    useEffect(() => {
        if (contactInformation.participantAdminProfile === constants.ADMIN_VIEW) {
            if (imageBlob) {
                setLogoSrc(imageBlob);
            }
        } else {
            if (userDetails.clientId > 0 && userDetails.defaultParticipantId > 0) {
                getPIdLogo(userDetails.defaultParticipantId, userDetails.clientId)
            }
        }
    }, [contactInformation.participantAdminProfile, userDetails.clientId, userDetails.defaultParticipantId, imageBlob])

    const handleImageClick = () => {
        const summaryRoles = [
            constants.ROLES_ADMIN_MANAGER,
            constants.ROLES_ADMIN_USER,
            constants.ROLES_PARTICIPANT_USER,
            constants.ROLES_READ_ONLY_USER,
            constants.ROLES_READ_ONLY_ALL_LOCATIONS,
            constants.ROLES_FINANCIAL_ANALYST,
            constants.ROLES_PROJECT_APPROVER
        ];

        const projectTasksRoles = [
            constants.ROLES_PROJECT_ANALYST,
            constants.ROLES_BUDGET_ANALYST
        ];
        const projectInvoiceRoles = [
            constants.ROLES_PROJECT_MANAGER,
        ];

        if (summaryRoles.includes(role)) {
            navigate("/summary");
        } else if (projectTasksRoles.includes(role)) {
            navigate("/invoiceTasksSummary");
        } else if (projectInvoiceRoles.includes(role)) {
            navigate("/vendorInvoiceSummary");
        }
    }
    async function getPIdLogo(pId: any, cId: number) {
        try {
            const responseLogo = await getByParticipantIdLogo(pId, cId);
            if (
                responseLogo &&
                responseLogo.status === constants.STATUS_CODES.SUCCESS &&
                responseLogo.data.data
            ) {
                setLogoSrc(`data:image/png;base64,${responseLogo.data.data}`);
            } else {
                if (imageBlob) {
                    setLogoSrc(imageBlob);
                }
            }
        } catch (error: any) {
            if (imageBlob) {
                setLogoSrc(imageBlob);
            }
        }
    }

    return (
        <ThemeProvider theme={theme} >
            <Box>
                <TopBar isTopView={view === "Top"}>
                    {!isSmallScreen ? (
                        <>
                            <Logo
                                src={logoSrc ?? "https://cdn.builder.io/api/v1/image/assets/TEMP/4a3999c4ec8a6ecc17d82dcbc724c027de5410a271c4aa649aba279cd7504ae6?placeholderIfAbsent=true&apiKey=2a10ec9e4ca34b8dabe8333a653afe75"}
                                alt="Company Logo"
                                loading="lazy"
                                style={{ cursor: 'pointer', width: 'auto', height: '55px' }}
                                onClick={handleImageClick}
                            />
                            {userDetails?.isLogin == true && <TopBarActions>
                                {/* <SearchBar onSearch={(value) => } /> */}

                                <UserProfile
                                    user={{
                                        name: userDetails.userName,
                                        role: userDetails?.roles?.roleName || '',
                                        avatar: `${userDetails.firstName.charAt(0).toUpperCase()}${userDetails.lastName.charAt(0).toUpperCase()}`,
                                        isLogin: userDetails.isLogin,
                                        tenantId: userDetails.tenantId,
                                        clientId: userDetails.clientId,
                                        defaultParticipantId: userDetails.defaultParticipantId,
                                        firstName: userDetails.firstName,
                                        lastName: userDetails.lastName
                                    }}
                                />
                            </TopBarActions>}
                        </>
                    ) : <>
                        {/* Row with Logo, Search Bar, Alert Icon, and UserProfile */}
                        <Row>
                            <Logo
                                src="https://cdn.builder.io/api/v1/image/assets/TEMP/4a3999c4ec8a6ecc17d82dcbc724c027de5410a271c4aa649aba279cd7504ae6?placeholderIfAbsent=true&apiKey=2a10ec9e4ca34b8dabe8333a653afe75"
                                alt="Company Logo"
                                loading="lazy"
                            />
                            {/* <SearchBar onSearch={(value) => } /> */}
                            {userDetails?.isLogin == true && <UserProfile
                                user={{
                                    name: userDetails.userName,
                                    role: userDetails?.roles?.roleName || '',
                                    avatar: `${userDetails.firstName.charAt(0).toUpperCase()}${userDetails.lastName.charAt(0).toUpperCase()}`,
                                    isLogin: userDetails.isLogin,
                                    tenantId: userDetails.tenantId,
                                    clientId: userDetails.clientId,
                                    defaultParticipantId: userDetails.defaultParticipantId,
                                    firstName: userDetails.firstName,
                                    lastName: userDetails.lastName
                                }}
                            />}
                        </Row>
                    </>
                    }
                </TopBar>
            </Box>
        </ThemeProvider>
    );
};
